import { MODEL_SETTING_KEYS, MODEL_TIERS, companyPolicyFromValues, tierRank } from "@uzverse/core";
import type { CompanyModelPolicy, EffortLevel, Locale, ModelTier } from "@uzverse/core";
import { useCallback, useEffect, useState } from "react";
import type {
  ModelCatalogResponse,
  SettingDescriptor,
  SettingValue,
  WebApi,
} from "../api/types.js";
import { useSession } from "./session.js";

const K = MODEL_SETTING_KEYS;

/** "Opus 5.5 — strongest, most expensive" -> "Opus 5.5". */
export const shortModelName = (label: string): string => label.split(" — ")[0] ?? label;

/** What the user and the company have configured, read from the module settings. */
export interface ModelPolicyView {
  catalog: ModelCatalogResponse | null;
  policy: CompanyModelPolicy;
  /** The assistant's own choice: "auto", a tier or a model id. */
  assistantModel: string;
  assistantEffort: "auto" | EffortLevel;
}

const valueOf = (list: SettingDescriptor[], key: string): SettingValue | undefined =>
  list.find((s) => s.key === key)?.value;

export function viewFromSettings(
  catalog: ModelCatalogResponse | null,
  settings: SettingDescriptor[],
): ModelPolicyView {
  const policy = companyPolicyFromValues((key) => valueOf(settings, key));
  const model = valueOf(settings, K.assistantModel);
  const effort = valueOf(settings, K.assistantEffort);
  return {
    catalog,
    policy,
    assistantModel: typeof model === "string" && model !== "" ? model : "auto",
    assistantEffort:
      typeof effort === "string" && (effort === "auto" || EFFORTS.includes(effort as EffortLevel))
        ? (effort as "auto" | EffortLevel)
        : "auto",
  };
}

export const EFFORTS: readonly EffortLevel[] = ["low", "medium", "high", "xhigh", "max"];

/** The model id a tier runs for this company (the company's choice, else the engine default). */
export function tierModelId(view: ModelPolicyView, tier: ModelTier): string | null {
  const own = view.policy.tiers[tier]?.trim();
  if (own) return own;
  return view.catalog?.models.find((m) => m.tiers.includes(tier))?.id ?? null;
}

/** Friendly model name for an id in `locale` (the id itself when unknown). */
export function modelLabel(view: ModelPolicyView, id: string, locale: Locale): string {
  return shortModelName(view.catalog?.models.find((m) => m.id === id)?.label[locale] ?? id);
}

/** Longer label with the descriptive tail ("Opus 5.5 — strongest, most expensive"). */
export function modelLongLabel(view: ModelPolicyView, id: string, locale: Locale): string {
  return view.catalog?.models.find((m) => m.id === id)?.label[locale] ?? id;
}

/** The tiers the user's assistant may use, cheapest first (the company's cost guard). */
export function assistantTiers(view: ModelPolicyView): ModelTier[] {
  const cap = tierRank(view.policy.assistantMaxTier);
  return MODEL_TIERS.filter((t) => tierRank(t) <= cap);
}

export const canChooseForAssistant = (v: ModelPolicyView): boolean => v.policy.allowAssistantChoice;
export const canOverridePerMessage = (v: ModelPolicyView): boolean => v.policy.allowMessageOverride;

const catalogs = new WeakMap<object, Promise<ModelCatalogResponse | null>>();

function catalogOf(api: WebApi): Promise<ModelCatalogResponse | null> {
  let catalog = catalogs.get(api);
  if (!catalog) {
    catalog = api.getModelCatalog().catch(() => null);
    catalogs.set(api, catalog);
  }
  return catalog;
}

/** The model catalog alone (cached per API client), for labels in the chat. */
export function useModelCatalog(): ModelCatalogResponse | null {
  const { services } = useSession();
  const [catalog, setCatalog] = useState<ModelCatalogResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    void catalogOf(services.api).then((c) => !cancelled && setCatalog(c));
    return () => {
      cancelled = true;
    };
  }, [services]);
  return catalog;
}

export interface ModelPolicyState {
  view: ModelPolicyView | null;
  /** Sets one of the user's `engine.assistant.*` settings. */
  setAssistant: (patch: { model?: string; effort?: "auto" | EffortLevel }) => Promise<void>;
}

/** Loads the model catalog and the model policy settings, and saves the user's own choice. */
export function useModelPolicy(): ModelPolicyState {
  const { services } = useSession();
  const [view, setView] = useState<ModelPolicyView | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([catalogOf(services.api), services.api.listSettings().catch(() => [])])
      .then(([c, settings]) => {
        if (!cancelled) setView(viewFromSettings(c, settings));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [services]);

  const setAssistant = useCallback(
    async (patch: { model?: string; effort?: "auto" | EffortLevel }) => {
      if (patch.model !== undefined) await services.api.setSetting(K.assistantModel, patch.model);
      if (patch.effort !== undefined)
        await services.api.setSetting(K.assistantEffort, patch.effort);
      setView((v) =>
        v
          ? {
              ...v,
              ...(patch.model !== undefined ? { assistantModel: patch.model } : {}),
              ...(patch.effort !== undefined ? { assistantEffort: patch.effort } : {}),
            }
          : v,
      );
    },
    [services],
  );

  return { view, setAssistant };
}
